import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { TbShoppingCart } from "react-icons/tb";
import { State } from "../../app/store";
import { FINALIZE_PAGE, PURCHASE, PURCHASE_FAILED } from "../../app/routes";
import EAButton from "../../app/components/shared/Buttons/EAButton";
import useParentViewport, { ParentViewport } from "../../utils/hooks/useParentViewport";
import useParentMessage from "../../utils/hooks/useParentMessage";
import useShowNavBar from "../NavBar/hooks/useShowNavBar";
import useDesktopSize from "../../utils/hooks/useDesktopSize";
import useBlockingLayer from "./useBlockingLayer";
import "./style.scss";

const BAR_MARGIN = 16;
const CHECKOUT_ROUTES = [FINALIZE_PAGE, PURCHASE, PURCHASE_FAILED];

const isBasketAnchorInView = (viewport: ParentViewport) =>
  Array.from(document.querySelectorAll<HTMLElement>("[data-basket-anchor]")).some((anchor) => {
    const rect = anchor.getBoundingClientRect();
    if (rect.height === 0) return false;
    const middle = rect.top + window.scrollY + rect.height / 2;
    return middle > viewport.top && middle < viewport.bottom;
  });

/**
 * Belka koszyka w iframe. Iframe ma wysokość całej treści, więc position: fixed przykleja się
 * do całej ramki, a nie do ekranu. Pozycję liczymy z widocznej części strony klienta.
 */
const FloatingBasketBar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const showNavBar = useShowNavBar();
  const { scrollToTop } = useParentMessage();
  const basketLength = useSelector((state: State) => state.basket.basketLength);
  const basketTotal = useSelector((state: State) => state.basket.basketTotal);
  const { viewport, isMoving, isLive } = useParentViewport();
  const isBlocked = useBlockingLayer();
  const isDesktop = useDesktopSize();
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);

  useLayoutEffect(() => {
    setBarHeight(barRef.current?.offsetHeight ?? 0);
  }, [viewport, basketLength, basketTotal, isDesktop]);

  const isCheckout = CHECKOUT_ROUTES.some((route) => location.pathname.startsWith(route));
  if (!viewport || basketLength === 0 || !showNavBar || isCheckout) return null;

  const isFrameInView = viewport.bottom > 0 && viewport.top < viewport.frameHeight;
  const isVisible = isFrameInView && !isBlocked && (isLive || !isMoving) && !isBasketAnchorInView(viewport);

  const maxTop = viewport.frameHeight - barHeight - BAR_MARGIN;
  const top = Math.max(BAR_MARGIN, Math.min(viewport.bottom - barHeight - BAR_MARGIN, maxTop));

  const handleGoToBasket = () => {
    navigate(FINALIZE_PAGE);
    scrollToTop();
  };

  return ReactDOM.createPortal(
    <div
      ref={barRef}
      className={`floating-basket-bar${isVisible ? " is-visible" : ""}${isLive ? " is-live" : ""}${
        isDesktop ? "" : " floating-basket-bar--compact"
      }`}
      style={{ transform: `translate3d(-50%, ${top}px, 0)` }}
      role="region"
      aria-label={t("koszyk")}
      aria-hidden={!isVisible}
    >
      <div className="floating-basket-bar__card">
        {isDesktop && (
          <div className="floating-basket-bar__summary">
            <span className="floating-basket-bar__count">{t("koszyk_belka_ilosc", { count: basketLength })}</span>
            {basketTotal > 0 && (
              <strong className="floating-basket-bar__total">
                {basketTotal.toFixed(2)} {t("zl")}
              </strong>
            )}
          </div>
        )}
        <EAButton title={t("przejdz_do_koszyka")} onClick={handleGoToBasket} startIcon={<TbShoppingCart />} />
      </div>
    </div>,
    document.body,
  );
};

export default FloatingBasketBar;
